import { classes, extend, media, stylesheet } from 'typestyle';
import { BlackOpacity10 } from '@traceair/tds';
import React, { useEffect } from 'react';
import { Customer, ScheduledFlight, Site } from '../../../../../types';
import loggerInstance from '../../../../../libs/logger/Logger';
import { BREAKPOINT_LARGE, BREAKPOINT_SMALL } from '../../../../layout/styles';
import { useUploadStore } from '../../../../../store/uploadStore';
import 'leaflet/dist/leaflet.css';
import { expandMapStyles, useExpandMap } from './useExpandMap';
import { useAddObservePanoramas } from './useAddObservePanoramas';
import { useAddSiteBoundary } from './useAddSiteBoundary';
import { useInitializeMap } from './useInitializeMap';
import { useDrawScanProgress } from './useDrawScanProgress';

const css = stylesheet({
  container: extend(
    {
      position: 'relative',
      zIndex: 0,
      flex: '0 0 auto',
      width: 206,
      height: 130,
      borderRadius: 8,
      border: `1px solid ${BlackOpacity10}`,
      overflow: 'hidden',
      $nest: {
        '&:hover': {
          $nest: {
            '.mission-map-expand': media({ minWidth: BREAKPOINT_LARGE }, { display: 'flex' }),
          },
        },
      },
    },
    media({ maxWidth: BREAKPOINT_SMALL }, { width: 96, height: 96 }),
  ),
  containerInOpenMission: {
    $nest: {
      '&&': {
        width: 230,
        height: 190,
      },
      '.mission-map-expand': media({ maxWidth: BREAKPOINT_LARGE }, { display: 'flex' }),
    },
  },
  map: extend({
    height: '100%',
    pointerEvents: 'none',
    touchAction: 'none',
    zIndex: 1,
  }),
});

interface Props {
  customer: Customer;
  site: Site;
  schedule?: ScheduledFlight;
  alwaysRender?: boolean;
  inOpenMission?: boolean;
}

export const MissionMap = ({ customer, site, schedule, alwaysRender = false, inOpenMission = false }: Props) => {
  const { getUpload } = useUploadStore();
  const scanUpload = getUpload(false, customer.id, site.id, schedule?.airtableRecordId);
  const panoUpload = getUpload(true, customer.id, site.id, schedule?.airtableRecordId);

  const { initializeMap, mapRef, mapInstanceRef } = useInitializeMap();
  const { addSiteBoundary, boundaryLayerRef, boundaryLayerRefBorder } = useAddSiteBoundary(site);

  const { expanded, toggleExpandButton } = useExpandMap(mapInstanceRef, boundaryLayerRef);

  const { addPanoramas, panoramasRef } = useAddObservePanoramas(site, expanded, panoUpload);

  const { drawScanProgress, circlesRef, updateForcer } = useDrawScanProgress(
    mapInstanceRef,
    scanUpload,
    boundaryLayerRefBorder,
    boundaryLayerRef,
  );

  const renderMap = () => {
    if (mapRef.current && !mapInstanceRef.current) {
      if (!site.boundary?.geometry?.coordinates?.length) {
        loggerInstance.error(`Site ${customer.id}/${site.id} boundary missing`);
        return;
      }

      initializeMap();
      addSiteBoundary(mapInstanceRef.current!);
      addPanoramas(mapInstanceRef.current!);
      drawScanProgress();
    }
  };

  const cleanUp = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      circlesRef.current = {};
      panoramasRef.current = [];
    }
  };

  useEffect(() => {
    drawScanProgress(true);
  }, [scanUpload, updateForcer]);

  useEffect(() => {
    if (alwaysRender) {
      renderMap();

      return cleanUp;
    }
  }, [alwaysRender, customer, site]);

  useEffect(() => {
    if (alwaysRender) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            renderMap();
          } else {
            cleanUp();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '160px',
      },
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    return () => {
      if (mapRef.current) {
        observer.unobserve(mapRef.current);
      }
      cleanUp();
    };
  }, [alwaysRender, customer, site, !!scanUpload, !!panoUpload]);

  return (
    <div
      className={classes(
        css.container,
        inOpenMission && css.containerInOpenMission,
        expanded && expandMapStyles.expandedContainer,
        expanded && 'mission-map-expanded',
      )}
    >
      {toggleExpandButton}
      <div ref={mapRef} className={css.map} />
    </div>
  );
};
